import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StyledButton, LoginWrapper, LoginBox, Logo, FieldWrapper, FieldLabel, StyledInput, ErrorText } from "./styled";
import { loginRequest, getCurrentUser } from "../api/auth";
import { useUser } from "../components/UserContext";
import { Visibility, VisibilityOff, Person, Lock } from "@mui/icons-material";
import { IconButton, InputAdornment, Fade, Zoom, Box, Typography } from "@mui/material";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useUser();

  useEffect(() => {
    setMounted(true);
    if (localStorage.getItem("access_token")) {
      navigate("/chat", { state: { justLoggedIn: true } });
    }
  }, [navigate]);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setError("");
    
    try {
      const token = await loginRequest(username, password);
      localStorage.setItem("access_token", token);
      const user = await getCurrentUser();
      setUser(user);
      
      navigate("/chat");
    } catch (err) {
      setError("Invalid username or password");
      // Clear any potentially stored tokens on error
      localStorage.removeItem("adminToken");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginWrapper>
      <Fade in={mounted} timeout={800}>
        <div>
          <Zoom in={mounted} timeout={600} style={{ transitionDelay: '200ms' }}>
            <LoginBox>
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Logo 
                  src="/assisto-white-color.png" 
                  alt="Logo" 
                  style={{ 
                    marginBottom: '12px',
                    height: '55px',   // берём из .logo-nav
                    width: 'auto',    // чтобы сохранить пропорции  
                  }}
                />


              </Box>

              {/* Error Message */}
              <Fade in={!!error} timeout={300}>
                <div>
                  {error && (
                    <ErrorText 
                      variant="body1"
                      sx={{
                        color: '#ff4444 !important', // Принудительно устанавливаем красный цвет
                        backgroundColor: 'rgba(255, 68, 68, 0.1)', // Легкий красный фон
                        border: '1px solid rgba(255, 68, 68, 0.4)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        textAlign: 'center',
                        mb: 2,
                        boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
                        animation: 'shake 0.5s ease-in-out',
                        '@keyframes shake': {
                          '0%, 100%': { transform: 'translateX(0)' },
                          '25%': { transform: 'translateX(-4px)' },
                          '75%': { transform: 'translateX(4px)' }
                        }
                      }}
                    >
                      ⚠️ {error}
                    </ErrorText>
                  )}
                </div>
              </Fade>

              {/* Username Field */}
              <FieldWrapper>
                <FieldLabel htmlFor="username">USERNAME</FieldLabel>
                <Box sx={{ position: 'relative' }}>
                  <StyledInput
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleKeyPress}
                    autoComplete="off"
                    placeholder="Enter your username"
                    style={{
                      paddingLeft: '40px',
                      paddingRight: '40px', // Такой же отступ как у password
                      transition: 'all 0.2s ease',
                      borderRadius: '6px',
                      width: '100%',
                      height: '48px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Person 
                    sx={{ 
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#b0b0b0',
                      fontSize: '18px'
                    }} 
                  />
                </Box>
              </FieldWrapper>

              {/* Password Field */}
              <FieldWrapper>
                <FieldLabel htmlFor="password">PASSWORD</FieldLabel>
                <Box sx={{ position: 'relative' }}>
                  <StyledInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyPress}
                    autoComplete="off"
                    placeholder="Enter your password"
                    style={{
                      paddingLeft: '40px',
                      paddingRight: '40px',
                      transition: 'all 0.2s ease',
                      borderRadius: '6px', // Убираем сильные углы
                      width: '100%',
                      height: '48px', // Фиксированная высота
                      boxSizing: 'border-box',
                    }}
                  />
                  <Lock 
                    sx={{ 
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#b0b0b0',
                      fontSize: '18px'
                    }} 
                  />
                  <IconButton
                    onClick={togglePasswordVisibility}
                    sx={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#b0b0b0',
                      padding: '4px',
                      '&:hover': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Box>
              </FieldWrapper>

              {/* Login Button */}
              <StyledButton 
                variant="contained" 
                onClick={handleLogin}
                disabled={isLoading}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover:before': {
                    left: '100%',
                  },
                  '&:disabled': {
                    opacity: 0.7,
                    cursor: 'not-allowed',
                  }
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                    SIGNING IN...
                  </Box>
                ) : (
                  'LOGIN'
                )}
              </StyledButton>

              {/* Footer */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
              
              </Box>
            </LoginBox>
          </Zoom>
        </div>
      </Fade>
    </LoginWrapper>
  );
};

export default Login;